import { Link } from "react-router-dom";
import { Mail, Copy, MessageSquareText, ShieldCheck, Smartphone, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { SUPPORT_EMAIL, SUPPORT_MAILTO, SOCIALS } from "@/lib/api";
import { DataSafety } from "@/components/DataSafety";

const PHONE_DISPLAY = "+91 97693 30271";
const PHONE_TEL = "+919769330271";
const ADDRESS = "A6-A9, New Haven, Palghar, Maharashtra - 401501, India";

const FAQS = [
  { q: "How do I get the TruCitizen app?", a: "Head to the home page, choose Android or iOS, and scan the QR code or use the download link. Store links go live with the public launch." },
  { q: "How fast will support reply?", a: "Emails with the [TruCitizen] prefix in the subject are auto-prioritised. Expect a response within 1–2 business days." },
  { q: "How do I request data deletion?", a: `Email ${SUPPORT_EMAIL} with the subject "[TruCitizen] Data Principal Request" from your registered email address, or use Profile → Delete account in the app.` },
  { q: "How do I report content or request a refund?", a: `Write to ${SUPPORT_EMAIL} with the subject "[TruCitizen] Report" or "[TruCitizen] Refund request" within 7 days of the transaction. If your purchase was made through the app store's own purchase system, the refund is handled by that store — the app shows you the correct route.` },
  { q: "What data does the app collect?", a: "Only what is needed to run your account and challenges: your name, email and user ID from Google or Apple sign-in, your optional profile photo, challenge activity, purchase history, crash logs and device identifiers. We never collect precise location, contacts, messages or card/UPI details, and we do not sell data or show ads. See the data-safety summary below." },
];

const QUICK = [
  { Icon: Smartphone, t: "App download", d: "Available from the home page.", to: "/", id: "help-quick-app" },
  { Icon: ShieldCheck, t: "Privacy", d: "Your rights & data requests.", to: "/privacy-policy", id: "help-quick-privacy" },
  { Icon: MessageSquareText, t: "Terms of Usage", d: "Plans, fair play, refunds.", to: "/terms", id: "help-quick-terms" },
];

export default function Help() {
  const copy = async () => {
    await navigator.clipboard.writeText(SUPPORT_EMAIL);
    toast.success("Email copied");
  };
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-32 sm:pt-36 pb-16 sm:pb-28" data-testid="help-page">
      <SEO title="Help & Support | TruCitizen" description="Get help with the TruCitizen app and website. Contact CreJik Technologies Pvt Ltd at support@crejik.com or +91 97693 30271. Use the [TruCitizen] subject prefix for faster responses." path="/help"
        jsonLd={[
          { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
          { "@context": "https://schema.org", "@type": "Organization", name: "CreJik Technologies Private Limited", brand: "TruCitizen", email: SUPPORT_EMAIL, telephone: PHONE_TEL, address: { "@type": "PostalAddress", streetAddress: "A6-A9, New Haven", addressLocality: "Palghar", addressRegion: "Maharashtra", postalCode: "401501", addressCountry: "IN" } },
        ]} />
      <div className="grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <p className="eyebrow mb-4">Help</p>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[var(--ink)]">We reply to humans, fast.</h1>
          <p className="mt-6 text-base md:text-lg text-[var(--ink-soft)] leading-relaxed">
            One inbox and one phone line for everything — app issues, data requests, press, partnerships. Add <span className="font-mono text-[var(--saffron-deep)]">[TruCitizen]</span> to your email subject and you jump the queue.
          </p>
          <div className="mt-8 glass rounded-3xl p-6 sm:p-8">
            <p className="eyebrow mb-3">Contact us at</p>
            <div className="flex flex-wrap items-center gap-3">
              <a href={SUPPORT_MAILTO} className="font-display font-bold text-xl sm:text-2xl text-[var(--ink)] break-all" data-testid="help-email-link">{SUPPORT_EMAIL}</a>
              <button onClick={copy} className="p-2 rounded-full border border-black/10 hover:bg-black/5" aria-label="Copy email" data-testid="help-copy-email"><Copy size={16} /></button>
            </div>
            <p className="mt-3 text-sm text-[var(--muted)]">Subject: <span className="font-mono text-[var(--ink)]">[TruCitizen] Your topic</span></p>
            <a href={SUPPORT_MAILTO} className="btn-saffron mt-6" data-testid="help-mail-button"><Mail size={18} /> Write to support</a>
            <div className="mt-8 pt-6 border-t border-black/[0.08] space-y-4">
              <p className="eyebrow">CreJik Technologies Pvt Ltd</p>
              <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-3 text-[var(--ink)] font-semibold hover:text-[var(--saffron-deep)] transition-colors" data-testid="help-phone-link">
                <Phone size={18} className="text-[var(--saffron)] shrink-0" /> {PHONE_DISPLAY}
              </a>
              <p className="flex items-start gap-3 text-sm text-[var(--ink-soft)] leading-relaxed" data-testid="help-address">
                <MapPin size={18} className="text-[var(--saffron)] shrink-0 mt-0.5" /> {ADDRESS}
              </p>
            </div>
          </div>
          <div className="mt-8 flex items-center gap-3">
            {SOCIALS.map((s) => (
              <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.name} data-testid={`help-social-${s.id}`}
                className="w-10 h-10 rounded-full border border-black/10 bg-white/60 flex items-center justify-center text-[var(--ink)] transition-all duration-300 hover:bg-[var(--ink)] hover:text-white hover:-translate-y-1">
                <i className={s.icon} aria-hidden="true" />
              </a>
            ))}
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          <Reveal delay={0.1} className="grid sm:grid-cols-3 gap-4 mb-10">
            {QUICK.map(({ Icon, t, d, to, id }) => (
              <Link key={t} to={to} data-testid={id} className="block glass rounded-2xl p-5 hover:-translate-y-1 transition-transform duration-300">
                <Icon size={20} className="text-[var(--saffron)]" />
                <p className="mt-3 font-bold text-[var(--ink)]">{t}</p>
                <p className="mt-1 text-sm text-[var(--muted)]">{d}</p>
              </Link>
            ))}
          </Reveal>
          <Reveal delay={0.2}>
            <p className="eyebrow mb-4">FAQ</p>
            <dl className="divide-y divide-black/[0.08]" data-testid="help-faqs">
              {FAQS.map((f) => (
                <div key={f.q} className="py-6">
                  <dt className="font-display font-bold text-lg text-[var(--ink)]">{f.q}</dt>
                  <dd className="mt-2 text-[var(--ink-soft)] leading-relaxed">{f.a}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
          <Reveal delay={0.25} className="mt-10">
            <DataSafety />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
